<template>
    <div class="mt-[44px] grid-cols-layout pb-5 lg:grid">
        <div class="mb-6 lg:mb-0 lg:mt-4">
            <h3
                class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
            >
                Ecomail
            </h3>
            <p class="max-w-xs text-sm text-gray-500">
                {{ t("vue.ecomail_pairing_token_description") }}
            </p>
        </div>

        <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
            <div class="flex w-full items-center p-6 border-b border-gray-200">
                <div class="text-gray-500 sm:w-2/12 shrink-0">
                    {{ t("vue.pairing_token") }}
                </div>
                <div
                    class="ml-2 flex max-w-xs overflow-hidden text-clip whitespace-nowrap text-gray-800 sm:ml-0 sm:w-10/12"
                >
                    <span v-for="(_, i) in Array(24)" :key="i">•</span>
                </div>
            </div>

            <div
                class="flex w-full items-center px-6 py-4 border-b border-gray-200"
            >
                <div class="text-gray-500 sm:w-2/12 shrink-0 text-sm">
                    {{ t("vue.status") }}
                </div>
                <div class="ml-2 flex items-center gap-2 sm:ml-0">
                    <div
                        :class="[
                            'h-2 w-2 rounded-full',
                            props.ecomailPartner
                                ? 'bg-green-500'
                                : 'bg-gray-300',
                        ]"
                    ></div>
                    <span class="text-sm text-gray-700">
                        <template v-if="props.ecomailPartner">
                            {{ t("vue.connected") }}
                            <span
                                v-if="props.ecomailPairedAt"
                                class="text-gray-400"
                            >
                                &ndash; {{ formatDate(props.ecomailPairedAt) }}
                            </span>
                        </template>
                        <template v-else>
                            {{ t("vue.not_connected") }}
                        </template>
                    </span>
                </div>
            </div>

            <div
                v-if="props.ecomailAccessTokenExpiresAt"
                class="flex w-full items-center px-6 py-4 border-b border-gray-200"
            >
                <div class="text-gray-500 sm:w-2/12 shrink-0 text-sm">
                    {{ t("vue.ecomail_access_token_expires_at") }}
                </div>
                <div class="ml-2 sm:ml-0 text-sm text-gray-700">
                    {{ formatDate(props.ecomailAccessTokenExpiresAt) }}
                </div>
            </div>

            <div class="flex justify-end gap-3">
                <div class="my-3 pr-6 flex items-center gap-3">
                    <AtomicButton
                        color="white"
                        size="sm"
                        :loading="regenerating"
                        @click="showRegenerateConfirm = true"
                    >
                        {{ t("vue.ecomail_regenerate_pairing_token") }}
                    </AtomicButton>
                    <AtomicButton
                        color="yellow"
                        size="sm"
                        @click="showToken = true"
                    >
                        {{ t("vue.show_token") }}
                    </AtomicButton>
                </div>
            </div>
        </div>
    </div>

    <Modal v-if="showToken" size="xl" @close="showToken = false">
        <template #header>
            <div class="text-gray-600">
                {{ t("vue.ecomail_pairing_token_modal_title") }}
            </div>
        </template>

        <template #body>
            <div class="p-6">
                <p class="mb-4 text-sm text-gray-500">
                    {{ t("vue.ecomail_pairing_token_modal_desc") }}
                </p>

                <div class="relative">
                    <textarea
                        style="resize: none"
                        rows="3"
                        readonly
                        class="mt-1 block w-full rounded-md border border-gray-300 bg-white pb-2.5 pl-3 pr-[22px] pt-2 text-sm leading-normal text-gray-800 shadow-sm font-mono"
                        :value="currentPairingToken"
                    ></textarea>

                    <transition
                        enter-active-class="transition duration-100 ease-out transform"
                        enter-from-class="scale-95 opacity-0"
                        enter-to-class="scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in transform"
                        leave-from-class="scale-100 opacity-100"
                        leave-to-class="scale-95 opacity-0"
                    >
                        <Tooltip
                            v-if="copiedSuccess"
                            class="absolute top-[-55px]"
                            style="left: 50%; transform: translate(-50%, 0)"
                        >
                            {{ t("vue.copied_to_clipboard") }}
                        </Tooltip>
                    </transition>
                </div>

                <AtomicButton
                    class="relative mt-5"
                    color="white"
                    size="lg"
                    @click="copyToken"
                >
                    <template #icon>
                        <div class="mr-2">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9 2C8.46957 2 7.96086 2.21071 7.58579 2.58579C7.21071 2.96086 7 3.46957 7 4V12C7 12.5304 7.21071 13.0391 7.58579 13.4142C7.96086 13.7893 8.46957 14 9 14H15C15.5304 14 16.0391 13.7893 16.4142 13.4142C16.7893 13.0391 17 12.5304 17 12V6.414C16.9999 5.88361 16.7891 5.37499 16.414 5L14 2.586C13.625 2.2109 13.1164 2.00011 12.586 2H9Z"
                                    fill="#9FA6B2"
                                />
                                <path
                                    d="M3 8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6V16H13C13 16.5304 12.7893 17.0391 12.4142 17.4142C12.0391 17.7893 11.5304 18 11 18H5C4.46957 18 3.96086 17.7893 3.58579 17.4142C3.21071 17.0391 3 16.5304 3 16V8Z"
                                    fill="#9FA6B2"
                                />
                            </svg>
                        </div>
                    </template>
                    {{ t("vue.copy_to_clipboard") }}
                </AtomicButton>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end border-t border-gray-200 p-6">
                <AtomicButton
                    size="md"
                    color="yellow"
                    @click="showToken = false"
                >
                    {{ t("vue.close") }}
                </AtomicButton>
            </div>
        </template>
    </Modal>

    <Modal
        v-if="showRegenerateConfirm"
        size="md"
        @close="showRegenerateConfirm = false"
    >
        <template #header>
            <div class="text-gray-600">
                {{ t("vue.ecomail_regenerate_confirm_title") }}
            </div>
        </template>

        <template #body>
            <div class="p-6 text-sm text-gray-600">
                {{ t("vue.ecomail_regenerate_confirm_desc") }}
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end gap-3 border-t border-gray-200 p-6">
                <AtomicButton
                    color="white"
                    size="md"
                    @click="showRegenerateConfirm = false"
                >
                    {{ t("vue.cancel") }}
                </AtomicButton>
                <AtomicButton
                    color="red"
                    size="md"
                    :loading="regenerating"
                    @click="regeneratePairingToken"
                >
                    {{ t("vue.ecomail_regenerate_pairing_token") }}
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import { useClipboard } from "@vueuse/core";
import { useTranslations } from "@/composables/useTranslations.ts";
import axios from "axios";

const props = defineProps<{
    pairingToken: string;
    ecomailPairedAt: string | null;
    ecomailPartner: string | null;
    ecomailAccessTokenExpiresAt: string | null;
}>();

const { t } = useTranslations();
const clipboard = useClipboard();
const showToken = ref(false);
const copiedSuccess = ref(false);
const showRegenerateConfirm = ref(false);
const regenerating = ref(false);
const currentPairingToken = ref(props.pairingToken);

const copyToken = () => {
    clipboard.copy(currentPairingToken.value);
    copiedSuccess.value = true;
    setTimeout(() => {
        copiedSuccess.value = false;
    }, 1000);
};

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });

const regeneratePairingToken = async () => {
    regenerating.value = true;
    showRegenerateConfirm.value = false;

    try {
        const response = await axios.post(
            route("settings.api-tokens.regenerate-pairing-token"),
        );
        currentPairingToken.value = response.data.pairing_token;
        showToken.value = true;
    } finally {
        regenerating.value = false;
    }
};
</script>
